import formatTimeAgo from '@orn/shared-web/utils/formatTimeAgo';
import * as React from 'react';

import Table from '../../../components/Table';

/**
 * Structural shape of a commit row as rendered by `CommitsBlock`. Both
 * `adminGetReleasePreview` and `adminGetReleaseChangeLog` return commits
 * matching this shape (they share the `ReleaseCommit` GraphQL type), so
 * the component is decoupled from any specific query's generated type.
 */
export interface IReleaseCommitRow {
  sha: string;
  shortSha: string;
  subject: string;
  author?: string | null;
  authoredAt: string;
  prNumber?: number | null;
  prUrl?: string | null;
}

interface ICommitsBlockProps {
  commits: IReleaseCommitRow[];
  baseTag: string;
  /**
   * Whether the commits list is expanded by default. Defaults to false
   * (the historical behavior in the full preview card). The /releases
   * idle CTA passes `true` so operators see the pending changes inline
   * without an extra click.
   */
  defaultExpanded?: boolean;
}

/**
 * Collapsed-by-default commits-since-last-release table. Operators usually
 * trust the LLM summary at the top of the preview card; the full commit
 * list is reference data they only need when something looks off.
 */
const CommitsBlock = ({ commits, baseTag, defaultExpanded = false }: ICommitsBlockProps) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-fg-tertiary hover:text-fg-secondary flex items-center gap-2 text-xs font-medium uppercase tracking-wider transition-colors"
      >
        <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
        <span>Commits ({commits.length})</span>
        <span className="text-fg-quaternary normal-case tracking-normal">since {baseTag}</span>
      </button>
      {expanded &&
        (commits.length === 0 ? (
          <p className="text-fg-tertiary text-xs italic">No commits since {baseTag}.</p>
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <Table.HeaderCell>PR / SHA</Table.HeaderCell>
                <Table.HeaderCell>Subject</Table.HeaderCell>
                <Table.HeaderCell>Author</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {commits.map((commit) => (
                <Table.Row key={commit.sha}>
                  <Table.Cell muted>{formatTimeAgo(commit.authoredAt)}</Table.Cell>
                  <Table.Cell mono>
                    {commit.prUrl && commit.prNumber != null ? (
                      <a
                        href={commit.prUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-fg-brand-primary hover:text-fg-brand-primary_alt underline underline-offset-2"
                      >
                        #{commit.prNumber}
                      </a>
                    ) : (
                      commit.shortSha
                    )}
                  </Table.Cell>
                  <Table.Cell>{commit.subject}</Table.Cell>
                  <Table.Cell mono muted>
                    {commit.author ?? '—'}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        ))}
    </div>
  );
};

export default CommitsBlock;
